import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  Play,
  Square,
  Server,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Settings,
  Clock,
  Box,
  Database,
  Cpu,
  HardDrive,
} from 'lucide-react';
import { useNamespaces, useStopNamespace, useStartNamespace } from '@/hooks/useNamespaces';
import {
  useEC2Instances,
  useRDSInstances,
  useStartEC2Instance,
  useStopEC2Instance,
  useStartRDSInstance,
  useStopRDSInstance,
} from '@/hooks/useAwsResources';
import PageHeader from '@/components/common/PageHeader';
import StatusBadge from '@/components/common/StatusBadge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorBanner from '@/components/common/ErrorBanner';
import type { EC2Instance, Namespace, NamespaceStatus, RDSInstance, WorkloadDetail } from '@/types';
import { clsx } from 'clsx';

// Schedule message shown in the banner at the top of the page.
// APScheduler jobs are live — this text stays in sync with the cron
// expressions in settings (namespace_weekday_stop_cron / start_cron).
const SCHEDULE_MESSAGE =
  'Non-production workloads auto-stop at 11:00 PM IST and restart at 9:00 AM IST on weekdays. On weekends, all workloads are completely stopped.';

type StatusFilter = 'all' | 'running' | 'stopped';

// EC2 state buckets — "running" includes the transient pending/stopping
// because the user thinks of "this box is alive and costing money."
// "stopped" is the only truly idle state.
const EC2_RUNNING_LIKE = new Set(['pending', 'running', 'stopping']);
// RDS bucketing — "available" + active transitions count as running-like.
const RDS_RUNNING_LIKE = new Set([
  'available',
  'starting',
  'stopping',
  'rebooting',
  'modifying',
  'backing-up',
]);

/**
 * Extract a useful detail message from an axios error. FastAPI returns
 * `{detail: "..."}` for HTTPException; fall back to the axios message
 * or a generic string.
 */
function extractError(err: unknown): { title: string; message: string; notConfigured: boolean } {
  if (err instanceof AxiosError) {
    const s = err.response?.status;
    const detail = err.response?.data?.detail;
    if (s === 503) {
      return {
        title: 'Environment management not set up yet',
        message: typeof detail === 'string' ? detail : 'EKS access is not configured on the backend.',
        notConfigured: true,
      };
    }
    if (typeof detail === 'string') {
      return { title: 'Failed to load namespaces', message: detail, notConfigured: false };
    }
  }
  return {
    title: 'Failed to load namespaces',
    message: err instanceof Error ? err.message : 'An unexpected error occurred.',
    notConfigured: false,
  };
}

// ------------------------------------------------------------------
// Shared helpers
// ------------------------------------------------------------------

/** Format millicores → human-readable core string. */
function fmtCpu(millicores: number): string {
  if (millicores === 0) return '0 cores';
  if (millicores < 1000) return `${millicores}m`;
  return `${(millicores / 1000).toFixed(millicores % 1000 === 0 ? 0 : 1)} core${millicores >= 2000 ? 's' : ''}`;
}

/** Format MiB → human-readable memory string. */
function fmtMem(mib: number): string {
  if (mib === 0) return '0 MiB';
  if (mib < 1024) return `${mib} MiB`;
  return `${(mib / 1024).toFixed(mib % 1024 === 0 ? 0 : 1)} GiB`;
}

/** Format GB (float) for EC2/RDS instance specs from pricing table. */
function fmtMemGb(gb: number): string {
  if (gb < 1) return `${gb * 1024} MiB`;
  return gb % 1 === 0 ? `${gb} GB` : `${gb} GB`;
}

/** Compact cost badge — shows hourly / daily / monthly in one line. */
function CostStrip({
  hourly,
  daily,
  monthly,
  source,
}: {
  hourly: number | null | undefined;
  daily: number | null | undefined;
  monthly: number | null | undefined;
  source?: 'vantage' | 'estimate';
}) {
  if (hourly == null || daily == null || monthly == null) return null;
  const prefix = source === 'vantage' ? '' : '~';
  return (
    <div className="flex items-center gap-1 flex-wrap text-xs text-slate-500 dark:text-slate-400">
      <span className="font-medium text-slate-700 dark:text-slate-300">
        {prefix}${hourly.toFixed(3)}/hr
      </span>
      <span className="text-slate-300 dark:text-slate-600">·</span>
      <span>{prefix}${daily.toFixed(2)}/day</span>
      <span className="text-slate-300 dark:text-slate-600">·</span>
      <span>{prefix}${monthly.toFixed(0)}/mo</span>
      {source === 'vantage' && (
        <span className="ml-1 rounded bg-emerald-50 px-1 py-0.5 text-emerald-600 ring-1 ring-emerald-200/80 text-[10px] dark:bg-emerald-950/40 dark:text-emerald-400 dark:ring-emerald-800/60">
          Vantage
        </span>
      )}
      {source === 'estimate' && (
        <span className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-slate-500 ring-1 ring-slate-200/80 text-[10px] dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700/60">
          list price
        </span>
      )}
    </div>
  );
}

/**
 * A row inside a namespace card showing one workload (Deployment or
 * StatefulSet). Compared to the previous version, we render small
 * "pod dots" — one dot per replica — so the user gets a visual sense of
 * scale at a glance. Dots fill emerald for ready pods and stay slate for
 * pending/missing pods. Above ~12 pods we collapse to a numeric badge so
 * the row doesn't spill horizontally.
 */
function WorkloadRow({ w }: { w: WorkloadDetail }) {
  const Icon = w.type === 'StatefulSet' ? Database : Box;
  const isHealthy = w.replicas > 0 && w.ready === w.replicas;
  const isStopped = w.replicas === 0;
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
        <span
          className="truncate font-mono text-slate-700 dark:text-slate-300"
          title={w.name}
        >
          {w.name}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <PodDots ready={w.ready} desired={w.replicas} />
        <span
          className={clsx(
            'font-mono text-[11px] font-medium tabular-nums',
            isHealthy && 'text-emerald-600 dark:text-emerald-400',
            isStopped && 'text-slate-400 dark:text-slate-500',
            !isHealthy && !isStopped && 'text-amber-600 dark:text-amber-400',
          )}
        >
          {w.ready}/{w.replicas}
        </span>
      </div>
    </div>
  );
}

/**
 * Render a row of small dots representing pods. Ready pods are filled
 * emerald, missing pods are outlined slate. Above MAX_DOTS we render the
 * first MAX_DOTS-1 and replace the last slot with a "+N" badge so the
 * row's width stays bounded.
 */
function PodDots({ ready, desired }: { ready: number; desired: number }) {
  const MAX_DOTS = 10;
  if (desired === 0) return null;

  if (desired <= MAX_DOTS) {
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: desired }, (_, i) => (
          <span
            key={i}
            className={clsx(
              'h-1.5 w-1.5 rounded-full transition-colors',
              i < ready
                ? 'bg-emerald-500 dark:bg-emerald-400'
                : 'bg-slate-300 dark:bg-slate-600',
            )}
          />
        ))}
      </div>
    );
  }

  // Collapsed mode: show ratio as a single pill
  const overflow = desired - (MAX_DOTS - 1);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: MAX_DOTS - 1 }, (_, i) => (
        <span
          key={i}
          className={clsx(
            'h-1.5 w-1.5 rounded-full',
            i < ready
              ? 'bg-emerald-500 dark:bg-emerald-400'
              : 'bg-slate-300 dark:bg-slate-600',
          )}
        />
      ))}
      <span className="ml-0.5 text-[9px] font-medium tabular-nums text-slate-400 dark:text-slate-500">
        +{overflow}
      </span>
    </div>
  );
}

/**
 * NamespaceCard — premium-redesign variant.
 *
 * Design priorities (ordered by what a user sees first when scanning a
 * grid of these cards):
 *
 *   1. STATUS    — ambient color tinting + capacity bar tell you the
 *                  state in <0.5s without reading any text
 *   2. COST      — promoted to the hero row; this is why the whole
 *                  feature exists, it should not be a footer
 *   3. CAPACITY  — running-pods-out-of-total as a real visual bar
 *   4. RESOURCES — CPU & Memory as compact side-by-side pills, not
 *                  rows of label/value
 *   5. WORKLOADS — workload names with pod-dot visualization
 *   6. ACTION    — single contextual button (Start XOR Stop depending
 *                  on current state) instead of always rendering both
 */
function NamespaceCard({
  ns,
  onStop,
  onStart,
  isActing,
}: {
  ns: Namespace;
  onStop: (name: string) => void;
  onStart: (name: string) => void;
  isActing: boolean;
}) {
  const canStop = ns.status === 'running' || ns.status === 'partial';
  const canStart = ns.status === 'stopped' || ns.status === 'partial';

  const workloads = ns.workloads ?? [];

  // Pod accounting — sum `ready` (actual K8s ready replica count) not
  // `replicas` (desired). Matches what `kubectl get pods` would show.
  const totalRunningPods = workloads.reduce((sum, w) => sum + w.ready, 0);
  const totalDesiredPods = workloads.reduce(
    (sum, w) => sum + Math.max(w.replicas, w.ready),
    0,
  );
  const cpuPerPodMillicores =
    totalRunningPods > 0 ? ns.cpu_requests_millicores / totalRunningPods : 0;
  const memPerPodMb =
    totalRunningPods > 0 ? ns.memory_requests_mb / totalRunningPods : 0;

  // Status-driven palette. We use it in 4 places: icon chip, ambient
  // gradient, capacity bar fill, and accent strip. Centralising keeps
  // them in lock-step.
  const palette = STATUS_PALETTES[ns.status];

  return (
    <div
      className={clsx(
        // Card shell — layered shadows for real depth, status-tinted
        // background gradient instead of flat white, refined border.
        'group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-300',
        'border-slate-200/60 dark:border-slate-800',
        'shadow-[0_1px_3px_rgba(15,23,42,0.04),0_4px_12px_-2px_rgba(15,23,42,0.04)]',
        'hover:-translate-y-1 hover:shadow-[0_4px_16px_-2px_rgba(15,23,42,0.08),0_12px_28px_-4px_rgba(15,23,42,0.06)]',
        'dark:shadow-[0_1px_3px_rgba(0,0,0,0.4),0_4px_12px_-2px_rgba(0,0,0,0.3)]',
        'dark:hover:shadow-[0_4px_16px_-2px_rgba(0,0,0,0.5),0_12px_28px_-4px_rgba(0,0,0,0.4)]',
        palette.cardBg,
      )}
    >
      {/* ── Ambient status gradient overlay ───────────────────────────
          Visible-but-tasteful radial bloom in the upper portion tinted
          by status. The card already has a subtle bg gradient; this
          adds a stronger atmospheric tint near the header so the eye
          immediately picks up the state. */}
      <div
        aria-hidden
        className={clsx(
          'pointer-events-none absolute inset-x-0 top-0 h-48',
          palette.gradient,
        )}
      />
      {/* Status accent strip down the left edge — now a soft vertical
          gradient instead of a flat colour so it reads as a designed
          element, not a 1px border accident. */}
      <div
        aria-hidden
        className={clsx('absolute left-0 top-0 h-full w-1', palette.accent)}
      />

      {/* ── Header row ───────────────────────────────────────────────── */}
      <div className="relative flex items-start justify-between gap-3 px-6 pb-3 pt-5">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={clsx(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset',
              palette.iconBg,
              palette.iconRing,
            )}
          >
            {isActing ? (
              <Loader2 className={clsx('h-5 w-5 animate-spin', palette.iconFg)} />
            ) : (
              <Server className={clsx('h-5 w-5', palette.iconFg)} />
            )}
          </div>
          <div className="min-w-0">
            <h3
              className="truncate font-mono text-base font-semibold text-slate-900 dark:text-slate-100"
              title={ns.name}
            >
              {ns.name}
            </h3>
            <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Kubernetes namespace
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <PulseBadge status={ns.status} palette={palette} />
          {ns.is_protected && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400"
              title="Protected — cannot be stopped"
            >
              <AlertTriangle className="h-3 w-3" />
              Protected
            </span>
          )}
        </div>
      </div>

      {/* ── Hero row: pods + cost ─────────────────────────────────────
          Both numbers now use *meaningful* colours instead of plain
          black/slate. Pods take the status palette colour when alive
          (emerald-700 when running). Cost gets a deep emerald-700 —
          the "money" colour, distinct from "success" green by weight
          and saturation. */}
      <div className="relative flex items-end justify-between gap-6 px-6 pb-4 pt-1">
        <div className="min-w-0">
          <p
            className={clsx(
              'text-4xl font-bold leading-none tabular-nums tracking-tight',
              totalRunningPods > 0
                ? palette.heroNumber
                : 'text-slate-400 dark:text-slate-500',
            )}
          >
            {totalRunningPods}
          </p>
          <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            {totalRunningPods === 1 ? 'pod running' : 'pods running'}
            {totalDesiredPods > totalRunningPods && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                · {totalDesiredPods - totalRunningPods} pending
              </span>
            )}
          </p>
        </div>

        {ns.estimated_monthly_cost_usd != null && (
          <div className="text-right">
            <p className="text-3xl font-bold leading-none tabular-nums tracking-tight text-emerald-700 dark:text-emerald-300">
              ${ns.estimated_monthly_cost_usd.toFixed(0)}
              <span className="ml-0.5 text-base font-semibold text-slate-400 dark:text-slate-500">
                /mo
              </span>
            </p>
            {ns.estimated_daily_cost_usd != null && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                ~${ns.estimated_daily_cost_usd.toFixed(2)}/day ·{' '}
                <span className="font-medium text-emerald-700/80 dark:text-emerald-400/80">
                  Vantage
                </span>
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Capacity bar — visual ready/desired ratio ─────────────────
          Hides when there are no workloads (empty namespace). Bar grows
          left-to-right; the fill width is the ready/desired percentage. */}
      {totalDesiredPods > 0 && (
        <div className="relative px-6 pb-4">
          <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className={clsx('h-full transition-all duration-500', palette.bar)}
              style={{
                width: `${Math.min(100, (totalRunningPods / totalDesiredPods) * 100)}%`,
              }}
            />
          </div>
          <div className="mt-1.5 flex justify-between text-[10px] font-medium tabular-nums text-slate-400 dark:text-slate-500">
            <span>
              {totalRunningPods} / {totalDesiredPods} ready
            </span>
            <span>
              {totalDesiredPods > 0
                ? `${Math.round((totalRunningPods / totalDesiredPods) * 100)}%`
                : ''}
            </span>
          </div>
        </div>
      )}

      {/* ── Resource pills — CPU and Memory side-by-side ──────────────
          Was: stacked rows of label/value. Now: two cards in a row that
          read like dashboard tiles. Hides cleanly when nothing's
          allocated (stopped namespace). */}
      {(ns.cpu_requests_millicores > 0 || ns.memory_requests_mb > 0) && (
        <div className="relative grid grid-cols-2 gap-2 px-6 pb-4">
          {ns.cpu_requests_millicores > 0 && (
            <ResourcePill
              icon={Cpu}
              label="CPU"
              total={fmtCpu(ns.cpu_requests_millicores)}
              perPod={
                totalRunningPods > 1
                  ? `~${fmtCpu(Math.round(cpuPerPodMillicores))}/pod`
                  : undefined
              }
            />
          )}
          {ns.memory_requests_mb > 0 && (
            <ResourcePill
              icon={HardDrive}
              label="Memory"
              total={fmtMem(ns.memory_requests_mb)}
              perPod={
                totalRunningPods > 1
                  ? `~${fmtMem(Math.round(memPerPodMb))}/pod`
                  : undefined
              }
            />
          )}
        </div>
      )}

      {/* ── Workload list ─────────────────────────────────────────────
          Soft top divider, slightly tinted background to set it apart
          from the hero/resources area above. Scrolls past ~4 workloads
          so a busy namespace doesn't blow the card up vertically. */}
      {workloads.length > 0 && (
        <div className="relative border-t border-slate-100 px-6 pb-4 pt-3 dark:border-slate-800">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {workloads.length === 1
              ? '1 workload'
              : `${workloads.length} workloads`}
          </p>
          <div className="max-h-32 space-y-2 overflow-y-auto pr-1">
            {workloads.map((w) => (
              <WorkloadRow key={`${w.type}/${w.name}`} w={w} />
            ))}
          </div>
        </div>
      )}

      {/* ── Contextual action ─────────────────────────────────────────
          One button, status-aware. Stopped namespaces show Start, running
          ones show Stop, partial ones show both. Reduces visual clutter
          and removes the "why is one of these always disabled?" question
          the previous design raised. */}
      {ns.status !== 'empty' && (
        <div className="relative mt-auto border-t border-slate-100 bg-surface-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
          {ns.status === 'partial' ? (
            <div className="flex gap-2">
              <ActionButton
                tone="start"
                onClick={() => onStart(ns.name)}
                disabled={!canStart || isActing}
                isActing={isActing}
              />
              <ActionButton
                tone="stop"
                onClick={() => onStop(ns.name)}
                disabled={!canStop || isActing || ns.is_protected}
                isActing={isActing}
                disabledTitle={
                  ns.is_protected
                    ? 'Protected namespace — cannot be stopped'
                    : undefined
                }
              />
            </div>
          ) : ns.status === 'running' ? (
            <ActionButton
              tone="stop"
              onClick={() => onStop(ns.name)}
              disabled={!canStop || isActing || ns.is_protected}
              isActing={isActing}
              full
              disabledTitle={
                ns.is_protected
                  ? 'Protected namespace — cannot be stopped'
                  : undefined
              }
            />
          ) : (
            <ActionButton
              tone="start"
              onClick={() => onStart(ns.name)}
              disabled={!canStart || isActing}
              isActing={isActing}
              full
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Per-status palette — designed with intentional colour psychology
 * instead of the generic "emerald = success" devtool template.
 *
 *   running   teal→emerald gradient with a deep emerald-700 hero number
 *             and a subtle ping-pulse on the status dot. Emerald = health
 *             and growth, the teal tilt makes it feel technical and
 *             modern instead of nineties-portal-green. The pulse signals
 *             "alive", not just "ok".
 *
 *   partial   amber → orange gradient. Warmer than pure yellow so it
 *             reads as "in progress / attention" rather than alarm.
 *             Distinguishes from a stop sign or warning siren.
 *
 *   stopped   cool slate. Slate communicates "at rest, intentional"
 *             rather than the broken/dead "gray" you'd see on a 503
 *             page. Keeps the card visually quiet.
 *
 *   empty     fully neutral — barely-there card, no decoration.
 *
 * Each entry carries everything a single status needs (card background,
 * ambient overlay, accent strip, capacity bar fill, status pill, icon
 * chip, hero number colour) so adding/changing a status is one edit.
 */
const STATUS_PALETTES: Record<
  NamespaceStatus,
  {
    cardBg: string;
    gradient: string;
    accent: string;
    bar: string;
    iconBg: string;
    iconRing: string;
    iconFg: string;
    heroNumber: string;
    statusBg: string;
    statusDot: string;
    statusText: string;
    pulse: boolean;
  }
> = {
  running: {
    cardBg:
      'bg-gradient-to-br from-white via-white to-emerald-50/50 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/20',
    gradient:
      'bg-gradient-to-br from-emerald-100/60 via-transparent to-transparent dark:from-emerald-900/30',
    accent:
      'bg-gradient-to-b from-teal-400 via-emerald-500 to-emerald-600 dark:from-teal-500 dark:via-emerald-500 dark:to-emerald-600',
    bar: 'bg-gradient-to-r from-emerald-400 to-emerald-500 dark:from-emerald-500 dark:to-emerald-400 shadow-[inset_0_-1px_0_rgba(16,185,129,0.4)]',
    iconBg: 'bg-emerald-50 dark:bg-emerald-950/40',
    iconRing: 'ring-emerald-200/60 dark:ring-emerald-900/40',
    iconFg: 'text-emerald-600 dark:text-emerald-400',
    heroNumber: 'text-emerald-700 dark:text-emerald-300',
    statusBg:
      'bg-emerald-50 text-emerald-700 ring-emerald-200/60 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/40',
    statusDot: 'bg-emerald-500 dark:bg-emerald-400',
    statusText: 'text-emerald-700 dark:text-emerald-300',
    pulse: true,
  },
  partial: {
    cardBg:
      'bg-gradient-to-br from-white via-white to-amber-50/60 dark:from-slate-900 dark:via-slate-900 dark:to-amber-950/20',
    gradient:
      'bg-gradient-to-br from-amber-100/50 via-transparent to-transparent dark:from-amber-900/30',
    accent:
      'bg-gradient-to-b from-orange-400 via-amber-500 to-amber-600 dark:from-orange-500 dark:via-amber-500 dark:to-amber-600',
    bar: 'bg-gradient-to-r from-amber-400 to-orange-500 dark:from-amber-500 dark:to-orange-500 shadow-[inset_0_-1px_0_rgba(245,158,11,0.4)]',
    iconBg: 'bg-amber-50 dark:bg-amber-950/40',
    iconRing: 'ring-amber-200/60 dark:ring-amber-900/40',
    iconFg: 'text-amber-600 dark:text-amber-400',
    heroNumber: 'text-amber-700 dark:text-amber-300',
    statusBg:
      'bg-amber-50 text-amber-700 ring-amber-200/60 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/40',
    statusDot: 'bg-amber-500 dark:bg-amber-400',
    statusText: 'text-amber-700 dark:text-amber-300',
    pulse: true,
  },
  stopped: {
    cardBg:
      'bg-gradient-to-br from-white via-white to-slate-100/70 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/40',
    gradient:
      'bg-gradient-to-br from-slate-100/40 via-transparent to-transparent dark:from-slate-800/40',
    accent: 'bg-gradient-to-b from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-700',
    bar: 'bg-gradient-to-r from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-500',
    iconBg: 'bg-slate-100 dark:bg-slate-800',
    iconRing: 'ring-slate-200/60 dark:ring-slate-700/60',
    iconFg: 'text-slate-400 dark:text-slate-500',
    heroNumber: 'text-slate-400 dark:text-slate-500',
    statusBg:
      'bg-slate-100 text-slate-600 ring-slate-200/60 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700/60',
    statusDot: 'bg-slate-400 dark:bg-slate-500',
    statusText: 'text-slate-600 dark:text-slate-400',
    pulse: false,
  },
  empty: {
    cardBg: 'bg-white dark:bg-slate-900',
    gradient: 'bg-transparent',
    accent: 'bg-transparent',
    bar: 'bg-slate-300 dark:bg-slate-600',
    iconBg: 'bg-slate-50 dark:bg-slate-800/60',
    iconRing: 'ring-slate-100 dark:ring-slate-800',
    iconFg: 'text-slate-300 dark:text-slate-600',
    heroNumber: 'text-slate-400 dark:text-slate-500',
    statusBg:
      'bg-slate-50 text-slate-500 ring-slate-200/60 dark:bg-slate-800/60 dark:text-slate-500 dark:ring-slate-700/60',
    statusDot: 'bg-slate-300 dark:bg-slate-600',
    statusText: 'text-slate-500 dark:text-slate-500',
    pulse: false,
  },
};

/**
 * Status pill rendered in the namespace card header. Unlike the shared
 * StatusBadge component used elsewhere, this one carries a small
 * coloured dot that **pulses** when the namespace is in a live state
 * (running / partial). The pulse is purely decorative — communicates
 * "alive" rather than "ok" — and stops on stopped/empty so the card
 * doesn't visually hum when there's nothing happening.
 */
function PulseBadge({
  status,
  palette,
}: {
  status: NamespaceStatus;
  palette: (typeof STATUS_PALETTES)[NamespaceStatus];
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium capitalize ring-1 ring-inset',
        palette.statusBg,
      )}
    >
      <span className="relative flex h-1.5 w-1.5">
        {palette.pulse && (
          <span
            className={clsx(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-70',
              palette.statusDot,
            )}
          />
        )}
        <span
          className={clsx(
            'relative inline-flex h-1.5 w-1.5 rounded-full',
            palette.statusDot,
          )}
        />
      </span>
      {status}
    </span>
  );
}

/**
 * Compact tile for a single resource (CPU or Memory). Icon + label
 * eyebrow, big value, optional per-pod suffix.
 */
function ResourcePill({
  icon: Icon,
  label,
  total,
  perPod,
}: {
  icon: typeof Cpu;
  label: string;
  total: string;
  perPod?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200/50 bg-white/80 px-3.5 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04),inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/80 dark:shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <p className="mt-1.5 font-mono text-sm font-semibold tabular-nums text-slate-800 dark:text-slate-200">
        {total}
      </p>
      {perPod && (
        <p className="text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
          {perPod}
        </p>
      )}
    </div>
  );
}

/**
 * Status-aware action button. Used both as the single full-width
 * action in standard cards (running → Stop, stopped → Start) and as a
 * paired button when the namespace is in a partial state.
 */
function ActionButton({
  tone,
  onClick,
  disabled,
  isActing,
  full = false,
  disabledTitle,
}: {
  tone: 'start' | 'stop';
  onClick: () => void;
  disabled: boolean;
  isActing: boolean;
  full?: boolean;
  disabledTitle?: string;
}) {
  const isStart = tone === 'start';
  const Icon = isStart ? Play : Square;
  const label = isStart ? 'Start namespace' : 'Stop namespace';
  const compactLabel = isStart ? 'Start' : 'Stop';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabledTitle}
      className={clsx(
        // Button base — tighter shadow, softer ring, subtle scale on tap
        'group/btn flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold ring-1 ring-inset transition-all duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40',
        full ? 'w-full' : 'flex-1',
        isStart
          ? // Start — emerald, gradient hover, subtle inner shadow
            'bg-gradient-to-b from-emerald-50 to-emerald-100/60 text-emerald-700 ring-emerald-200/70 shadow-[0_1px_0_rgba(16,185,129,0.1),inset_0_1px_0_rgba(255,255,255,0.6)] hover:from-emerald-100 hover:to-emerald-100 hover:ring-emerald-300 dark:from-emerald-950/40 dark:to-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-900/50 dark:shadow-none dark:hover:from-emerald-950/60 dark:hover:to-emerald-950/50'
          : // Stop — rose-50, NOT harsh red. Stop is a routine action,
            // not an emergency. Rose has the same "danger" semantic but
            // reads quieter; the gradient + ring give it more depth
            // than the previous flat red-50.
            'bg-gradient-to-b from-rose-50 to-rose-100/60 text-rose-700 ring-rose-200/70 shadow-[0_1px_0_rgba(244,63,94,0.1),inset_0_1px_0_rgba(255,255,255,0.6)] hover:from-rose-100 hover:to-rose-100 hover:ring-rose-300 dark:from-rose-950/40 dark:to-rose-950/30 dark:text-rose-300 dark:ring-rose-900/50 dark:shadow-none dark:hover:from-rose-950/60 dark:hover:to-rose-950/50',
      )}
    >
      {isActing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icon className="h-3.5 w-3.5 transition-transform group-hover/btn:scale-110" />
      )}
      {full ? label : compactLabel}
    </button>
  );
}

/**
 * One filter chip. Active state uses a stronger color and adds a ring.
 */
function FilterChip({
  active,
  onClick,
  count,
  label,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  label: string;
  tone: 'emerald' | 'slate' | 'neutral';
}) {
  const toneStyles = {
    emerald: {
      active: 'bg-emerald-100 border-emerald-300 text-emerald-800 ring-2 ring-emerald-200 dark:bg-emerald-900/40 dark:border-emerald-700 dark:text-emerald-300 dark:ring-emerald-800',
      idle: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/40',
    },
    slate: {
      active: 'bg-slate-200 border-slate-400 text-slate-900 ring-2 ring-slate-300 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-100 dark:ring-slate-600',
      idle: 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700',
    },
    neutral: {
      active: 'bg-slate-200 border-slate-400 text-slate-900 ring-2 ring-slate-300 dark:bg-slate-700 dark:border-slate-500 dark:text-slate-100 dark:ring-slate-600',
      idle: 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500 dark:hover:bg-slate-700',
    },
  } as const;
  const styles = active ? toneStyles[tone].active : toneStyles[tone].idle;

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx('rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors', styles)}
    >
      {count} {label}
    </button>
  );
}

// ------------------------------------------------------------------
// EC2 instance card
// ------------------------------------------------------------------

function ec2StateTone(state: string): { bg: string; text: string; badge: string } {
  switch (state) {
    case 'running':
      return {
        bg: 'bg-emerald-100 dark:bg-emerald-950/40',
        text: 'text-emerald-600 dark:text-emerald-400',
        badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/60',
      };
    case 'stopped':
      return {
        bg: 'bg-slate-100 dark:bg-slate-800',
        text: 'text-slate-400 dark:text-slate-500',
        badge: 'bg-slate-100 text-slate-600 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700/60',
      };
    case 'stopping':
    case 'shutting-down':
      return {
        bg: 'bg-amber-100 dark:bg-amber-950/40',
        text: 'text-amber-600 dark:text-amber-400',
        badge: 'bg-amber-50 text-amber-700 ring-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/60',
      };
    case 'pending':
      return {
        bg: 'bg-sky-100 dark:bg-sky-950/40',
        text: 'text-sky-600 dark:text-sky-400',
        badge: 'bg-sky-50 text-sky-700 ring-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/60',
      };
    default:
      return {
        bg: 'bg-slate-50 dark:bg-slate-800/60',
        text: 'text-slate-400 dark:text-slate-500',
        badge: 'bg-slate-100 text-slate-500 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700/60',
      };
  }
}

function EC2Card({
  instance,
  onStart,
  onStop,
  isActing,
}: {
  instance: EC2Instance;
  onStart: (id: string) => void;
  onStop: (id: string) => void;
  isActing: boolean;
}) {
  const tone = ec2StateTone(instance.state);
  const canStart = instance.state === 'stopped';
  const canStop = instance.state === 'running';
  const label = instance.name || instance.instance_id;

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-card overflow-hidden flex flex-col transition-all hover:shadow-card-hover hover:border-slate-200 dark:bg-slate-900 dark:border-slate-800/80 dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)] dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.4)] dark:hover:border-slate-700">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between dark:border-slate-800">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tone.bg}`}>
            {isActing ? (
              <Loader2 className={`h-5 w-5 ${tone.text} animate-spin`} />
            ) : (
              <Cpu className={`h-5 w-5 ${tone.text}`} />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate" title={label}>
              {label}
            </h3>
            {instance.name && (
              <span className="font-mono text-xs text-slate-400 dark:text-slate-500">{instance.instance_id}</span>
            )}
          </div>
        </div>
        <span
          className={clsx(
            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize',
            tone.badge,
          )}
        >
          {instance.state}
        </span>
      </div>

      <div className="px-6 py-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500 dark:text-slate-400">Type</span>
          <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">{instance.instance_type}</span>
        </div>
        {instance.vcpus != null && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">vCPU · RAM</span>
            <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">
              {instance.vcpus} vCPU · {instance.memory_gb != null ? fmtMemGb(instance.memory_gb) : '—'}
            </span>
          </div>
        )}
        {instance.private_ip && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Private IP</span>
            <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">{instance.private_ip}</span>
          </div>
        )}
        {instance.public_ip && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Public IP</span>
            <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">{instance.public_ip}</span>
          </div>
        )}
        {instance.estimated_hourly_cost_usd != null && (
          <div className={clsx('pt-1 border-t border-slate-100 dark:border-slate-800', instance.state !== 'running' && 'opacity-40')}>
            <CostStrip
              hourly={instance.estimated_hourly_cost_usd}
              daily={instance.estimated_daily_cost_usd}
              monthly={instance.estimated_monthly_cost_usd}
              source="estimate"
            />
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-100 bg-surface-50/60 flex gap-2 mt-auto dark:border-slate-800 dark:bg-slate-950/60">
        <button
          onClick={() => onStart(instance.instance_id)}
          disabled={!canStart || isActing}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:text-emerald-400 dark:bg-slate-900 dark:border-emerald-800 dark:hover:bg-emerald-950/40"
        >
          <Play className="h-3.5 w-3.5" />
          Start
        </button>
        <button
          onClick={() => onStop(instance.instance_id)}
          disabled={!canStop || isActing}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-rose-700 bg-white border border-rose-200 rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:text-rose-400 dark:bg-slate-900 dark:border-rose-800 dark:hover:bg-rose-950/40"
        >
          <Square className="h-3.5 w-3.5" />
          Stop
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// RDS instance card
// ------------------------------------------------------------------

function rdsStatusTone(status: string): { bg: string; text: string; badge: string } {
  switch (status) {
    case 'available':
      return {
        bg: 'bg-emerald-100 dark:bg-emerald-950/40',
        text: 'text-emerald-600 dark:text-emerald-400',
        badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/60',
      };
    case 'stopped':
      return {
        bg: 'bg-slate-100 dark:bg-slate-800',
        text: 'text-slate-400 dark:text-slate-500',
        badge: 'bg-slate-100 text-slate-600 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700/60',
      };
    case 'stopping':
    case 'rebooting':
      return {
        bg: 'bg-amber-100 dark:bg-amber-950/40',
        text: 'text-amber-600 dark:text-amber-400',
        badge: 'bg-amber-50 text-amber-700 ring-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/60',
      };
    case 'starting':
    case 'backing-up':
    case 'modifying':
      return {
        bg: 'bg-sky-100 dark:bg-sky-950/40',
        text: 'text-sky-600 dark:text-sky-400',
        badge: 'bg-sky-50 text-sky-700 ring-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/60',
      };
    default:
      return {
        bg: 'bg-slate-50 dark:bg-slate-800/60',
        text: 'text-slate-400 dark:text-slate-500',
        badge: 'bg-slate-100 text-slate-500 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700/60',
      };
  }
}

function RDSCard({
  instance,
  onStart,
  onStop,
  isActing,
}: {
  instance: RDSInstance;
  onStart: (id: string) => void;
  onStop: (id: string) => void;
  isActing: boolean;
}) {
  const tone = rdsStatusTone(instance.status);
  const canStart = instance.status === 'stopped';
  const canStop = instance.status === 'available';

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-card overflow-hidden flex flex-col transition-all hover:shadow-card-hover hover:border-slate-200 dark:bg-slate-900 dark:border-slate-800/80 dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)] dark:hover:shadow-[0_4px_12px_rgba(0,0,0,0.4)] dark:hover:border-slate-700">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between dark:border-slate-800">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tone.bg}`}>
            {isActing ? (
              <Loader2 className={`h-5 w-5 ${tone.text} animate-spin`} />
            ) : (
              <HardDrive className={`h-5 w-5 ${tone.text}`} />
            )}
          </div>
          <div className="min-w-0">
            <h3
              className="text-base font-semibold text-slate-900 dark:text-slate-100 font-mono truncate"
              title={instance.db_identifier}
            >
              {instance.db_identifier}
            </h3>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              {instance.engine} {instance.engine_version}
            </span>
          </div>
        </div>
        <span
          className={clsx(
            'inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize',
            tone.badge,
          )}
        >
          {instance.status}
        </span>
      </div>

      <div className="px-6 py-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500 dark:text-slate-400">Class</span>
          <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">{instance.instance_class}</span>
        </div>
        {instance.vcpus != null && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">vCPU · RAM</span>
            <span className="font-mono text-slate-700 dark:text-slate-300 text-xs">
              {instance.vcpus} vCPU · {instance.memory_gb != null ? fmtMemGb(instance.memory_gb) : '—'}
            </span>
          </div>
        )}
        {instance.allocated_storage_gb != null && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Storage</span>
            <span className="text-slate-700 dark:text-slate-300 text-sm">{instance.allocated_storage_gb} GB gp2</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-slate-500 dark:text-slate-400">Multi-AZ</span>
          <span className={`text-sm font-medium ${instance.multi_az ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
            {instance.multi_az ? 'Yes' : 'No'}
          </span>
        </div>
        {instance.endpoint && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">Endpoint</span>
            <span
              className="font-mono text-slate-600 dark:text-slate-400 text-xs truncate max-w-[180px]"
              title={instance.endpoint}
            >
              {instance.endpoint}
            </span>
          </div>
        )}
        {instance.estimated_hourly_cost_usd != null && (
          <div className={clsx('pt-1 border-t border-slate-100 dark:border-slate-800', instance.status !== 'available' && 'opacity-40')}>
            <CostStrip
              hourly={instance.estimated_hourly_cost_usd}
              daily={instance.estimated_daily_cost_usd}
              monthly={instance.estimated_monthly_cost_usd}
              source="estimate"
            />
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-100 bg-surface-50/60 flex gap-2 mt-auto dark:border-slate-800 dark:bg-slate-950/60">
        <button
          onClick={() => onStart(instance.db_identifier)}
          disabled={!canStart || isActing}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:text-emerald-400 dark:bg-slate-900 dark:border-emerald-800 dark:hover:bg-emerald-950/40"
        >
          <Play className="h-3.5 w-3.5" />
          Start
        </button>
        <button
          onClick={() => onStop(instance.db_identifier)}
          disabled={!canStop || isActing}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-rose-700 bg-white border border-rose-200 rounded-lg hover:bg-rose-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed dark:text-rose-400 dark:bg-slate-900 dark:border-rose-800 dark:hover:bg-rose-950/40"
        >
          <Square className="h-3.5 w-3.5" />
          Stop
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Section header shared by EC2 and RDS panels
// ------------------------------------------------------------------

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  isFetching,
  onRefresh,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        <div>
          <h2 className="text-section-title text-slate-900 dark:text-slate-100">{title}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
      </div>
      <button
        onClick={onRefresh}
        disabled={isFetching}
        className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-surface-50 disabled:opacity-50 transition-colors dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-800"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );
}

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------

export default function Environments() {
  const [actingNamespace, setActingNamespace] = useState<string | null>(null);
  const [actingEC2, setActingEC2] = useState<string | null>(null);
  const [actingRDS, setActingRDS] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  // Independent filter state per section so toggling EC2 to "running"
  // doesn't drag the namespace or RDS lists along with it.
  const [ec2Filter, setEc2Filter] = useState<StatusFilter>('all');
  const [rdsFilter, setRdsFilter] = useState<StatusFilter>('all');

  // Kubernetes namespaces
  const { data: namespaces, isLoading, error, refetch, isFetching } = useNamespaces();
  const stopNs = useStopNamespace();
  const startNs = useStartNamespace();

  // EC2
  const { data: ec2Instances, isLoading: ec2Loading, error: ec2Error, refetch: ec2Refetch, isFetching: ec2Fetching } =
    useEC2Instances();
  const startEC2 = useStartEC2Instance();
  const stopEC2 = useStopEC2Instance();

  // RDS
  const { data: rdsInstances, isLoading: rdsLoading, error: rdsError, refetch: rdsRefetch, isFetching: rdsFetching } =
    useRDSInstances();
  const startRDS = useStartRDSInstance();
  const stopRDS = useStopRDSInstance();

  const handleStop = (name: string) => {
    setActingNamespace(name);
    stopNs.mutate(name, { onSettled: () => setActingNamespace(null) });
  };

  const handleStart = (name: string) => {
    setActingNamespace(name);
    startNs.mutate(name, { onSettled: () => setActingNamespace(null) });
  };

  const handleEC2Start = (id: string) => {
    setActingEC2(id);
    startEC2.mutate(id, { onSettled: () => setActingEC2(null) });
  };

  const handleEC2Stop = (id: string) => {
    setActingEC2(id);
    stopEC2.mutate(id, { onSettled: () => setActingEC2(null) });
  };

  const handleRDSStart = (id: string) => {
    setActingRDS(id);
    startRDS.mutate(id, { onSettled: () => setActingRDS(null) });
  };

  const handleRDSStop = (id: string) => {
    setActingRDS(id);
    stopRDS.mutate(id, { onSettled: () => setActingRDS(null) });
  };

  if (isLoading) return <LoadingSpinner message="Loading namespaces..." />;
  if (error) {
    const info = extractError(error);
    if (info.notConfigured) {
      return (
        <div className="space-y-6">
          <PageHeader
            title="Environments"
            description="Start and stop Kubernetes namespaces, EC2 instances, and RDS databases in the dev account"
          />
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950/30">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-amber-100 p-2.5 dark:bg-amber-900/40">
                <Settings className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-amber-900 dark:text-amber-200">{info.title}</h3>
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">{info.message}</p>
                <div className="mt-4 space-y-2 text-sm text-amber-900 dark:text-amber-200">
                  <p className="font-medium">To enable this feature, complete the AWS setup:</p>
                  <ol className="ml-4 list-decimal space-y-1 text-amber-800 dark:text-amber-300">
                    <li>Create an IAM user with EKS access in the target AWS account</li>
                    <li>
                      Map the user into the EKS{' '}
                      <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-xs dark:bg-amber-900/40">aws-auth</code> ConfigMap
                    </li>
                    <li>
                      Apply{' '}
                      <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-xs dark:bg-amber-900/40">
                        kubernetes/base/namespace-manager-rbac.yaml
                      </code>{' '}
                      to the EKS cluster
                    </li>
                    <li>
                      Store AWS keys in GCP Secret Manager and sync them into the{' '}
                      <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-xs dark:bg-amber-900/40">idp-portal-eks-secrets</code>{' '}
                      Secret
                    </li>
                    <li>Restart the API pod to pick up the new env vars</li>
                  </ol>
                </div>
                <button
                  onClick={() => refetch()}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Check again
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <ErrorBanner title={info.title} message={info.message} onRetry={refetch} />;
  }

  const items: Namespace[] = namespaces ?? [];
  const isRunningLike = (s: NamespaceStatus) => s === 'running' || s === 'partial';
  const runningCount = items.filter((ns) => isRunningLike(ns.status)).length;
  const stoppedCount = items.filter((ns) => ns.status === 'stopped').length;

  const filtered = items.filter((ns) => {
    if (filter === 'all') return true;
    if (filter === 'running') return isRunningLike(ns.status);
    if (filter === 'stopped') return ns.status === 'stopped';
    return true;
  });

  const ec2Items: EC2Instance[] = ec2Instances ?? [];
  const rdsItems: RDSInstance[] = rdsInstances ?? [];

  // Counts for the EC2 filter chips. "running" here means any non-stopped
  // state (running/pending/stopping) — those all incur cost.
  const ec2RunningCount = ec2Items.filter((i) => EC2_RUNNING_LIKE.has(i.state)).length;
  const ec2StoppedCount = ec2Items.filter((i) => i.state === 'stopped').length;
  const ec2Filtered = ec2Items.filter((i) => {
    if (ec2Filter === 'all') return true;
    if (ec2Filter === 'running') return EC2_RUNNING_LIKE.has(i.state);
    if (ec2Filter === 'stopped') return i.state === 'stopped';
    return true;
  });

  // RDS counts mirror the EC2 pattern.
  const rdsRunningCount = rdsItems.filter((i) => RDS_RUNNING_LIKE.has(i.status)).length;
  const rdsStoppedCount = rdsItems.filter((i) => i.status === 'stopped').length;
  const rdsFiltered = rdsItems.filter((i) => {
    if (rdsFilter === 'all') return true;
    if (rdsFilter === 'running') return RDS_RUNNING_LIKE.has(i.status);
    if (rdsFilter === 'stopped') return i.status === 'stopped';
    return true;
  });

  return (
    <div className="space-y-10 animate-fade-in">
      <PageHeader
        title="Environments"
        description="Start and stop Kubernetes namespaces, EC2 instances, and RDS databases in the dev account"
        actions={
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Scheduled-stops info banner. APScheduler jobs are live. */}
      <div className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200">
        <Clock className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
        <p className="text-sm text-sky-900 dark:text-sky-200">
          <span className="font-medium">Scheduled:</span> {SCHEDULE_MESSAGE}
        </p>
      </div>

      {/* ----------------------------------------------------------------
          Kubernetes Namespaces section
      ---------------------------------------------------------------- */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server className="h-5 w-5 text-slate-500 dark:text-slate-400" />
            <div>
              <h2 className="text-section-title text-slate-900 dark:text-slate-100">Kubernetes Namespaces</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">EKS workloads — scale-to-zero start/stop</p>
            </div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex gap-3 flex-wrap">
          <FilterChip
            active={filter === 'running'}
            onClick={() => setFilter(filter === 'running' ? 'all' : 'running')}
            count={runningCount}
            label="running"
            tone="emerald"
          />
          <FilterChip
            active={filter === 'stopped'}
            onClick={() => setFilter(filter === 'stopped' ? 'all' : 'stopped')}
            count={stoppedCount}
            label="stopped"
            tone="slate"
          />
          <FilterChip
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            count={items.length}
            label="total"
            tone="neutral"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((ns) => (
            <NamespaceCard
              key={ns.name}
              ns={ns}
              onStop={handleStop}
              onStart={handleStart}
              isActing={actingNamespace === ns.name}
            />
          ))}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16 text-slate-400 dark:text-slate-500">
            <Server className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium dark:text-slate-400">No manageable namespaces found</p>
            <p className="text-sm mt-1 dark:text-slate-500">System and protected namespaces are hidden by default</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400 dark:text-slate-500">
            <Server className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium">No namespaces match the current filter</p>
            <button
              onClick={() => setFilter('all')}
              className="mt-3 text-sm text-slate-600 underline hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Show all namespaces
            </button>
          </div>
        ) : null}
      </section>

      <hr className="border-slate-200 dark:border-slate-800" />

      {/* ----------------------------------------------------------------
          EC2 Instances section
      ---------------------------------------------------------------- */}
      <section className="space-y-4">
        <SectionHeader
          icon={Cpu}
          title="EC2 Instances"
          subtitle="Dev account — filtered by Environment=dev tag"
          isFetching={ec2Fetching}
          onRefresh={ec2Refetch}
        />

        {ec2Loading ? (
          <LoadingSpinner message="Loading EC2 instances..." />
        ) : ec2Error ? (
          (() => {
            const isUnconfigured =
              ec2Error instanceof AxiosError && ec2Error.response?.status === 503;
            return isUnconfigured ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                <span className="font-medium">AWS credentials not configured.</span>{' '}
                {String((ec2Error as AxiosError).response?.data?.detail ?? 'Set AWS_ACCESS_KEY_ID and AWS_SECRET_ACCESS_KEY.')}
              </div>
            ) : (
              <ErrorBanner
                title="Failed to load EC2 instances"
                message={ec2Error instanceof Error ? ec2Error.message : 'Request failed'}
                onRetry={ec2Refetch}
              />
            );
          })()
        ) : ec2Items.length === 0 ? (
          <div className="rounded-2xl border border-slate-200/60 bg-surface-50 px-5 py-10 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            <Cpu className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium text-slate-600 dark:text-slate-300">
              No EC2 instances tagged <code className="font-mono">Environment=dev</code>
            </p>
            <p className="text-sm mt-2 text-slate-500 dark:text-slate-400">
              Tag a dev EC2 instance to manage it here:
            </p>
            <pre className="mt-3 mx-auto inline-block text-left rounded bg-slate-900 px-3 py-2 text-xs font-mono text-slate-100 dark:bg-slate-800">
              aws ec2 create-tags --resources i-xxxxxxxx \{'\n'}
              {'  '}--tags Key=Environment,Value=dev
            </pre>
          </div>
        ) : (
          <>
            {/* Filter chips — same pattern as namespaces section. */}
            <div className="flex gap-3 flex-wrap">
              <FilterChip
                active={ec2Filter === 'running'}
                onClick={() => setEc2Filter(ec2Filter === 'running' ? 'all' : 'running')}
                count={ec2RunningCount}
                label="running"
                tone="emerald"
              />
              <FilterChip
                active={ec2Filter === 'stopped'}
                onClick={() => setEc2Filter(ec2Filter === 'stopped' ? 'all' : 'stopped')}
                count={ec2StoppedCount}
                label="stopped"
                tone="slate"
              />
              <FilterChip
                active={ec2Filter === 'all'}
                onClick={() => setEc2Filter('all')}
                count={ec2Items.length}
                label="total"
                tone="neutral"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {ec2Filtered.map((inst) => (
                <EC2Card
                  key={inst.instance_id}
                  instance={inst}
                  onStart={handleEC2Start}
                  onStop={handleEC2Stop}
                  isActing={actingEC2 === inst.instance_id}
                />
              ))}
            </div>

            {ec2Filtered.length === 0 && (
              <div className="text-center py-10 text-slate-400 dark:text-slate-500">
                <Cpu className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-base font-medium">No EC2 instances match the current filter</p>
                <button
                  onClick={() => setEc2Filter('all')}
                  className="mt-3 text-sm text-slate-600 underline hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  Show all instances
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <hr className="border-slate-200 dark:border-slate-800" />

      {/* ----------------------------------------------------------------
          RDS Instances section
      ---------------------------------------------------------------- */}
      <section className="space-y-4">
        <SectionHeader
          icon={HardDrive}
          title="RDS Databases"
          subtitle="Dev account — filtered by Environment=dev tag"
          isFetching={rdsFetching}
          onRefresh={rdsRefetch}
        />

        {rdsLoading ? (
          <LoadingSpinner message="Loading RDS instances..." />
        ) : rdsError ? (
          (() => {
            const isUnconfigured =
              rdsError instanceof AxiosError && rdsError.response?.status === 503;
            return isUnconfigured ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                <span className="font-medium">AWS credentials not configured.</span>{' '}
                {String((rdsError as AxiosError).response?.data?.detail ?? 'Set AWS_ACCESS_KEY_ID and AWS_SECRET_ACCESS_KEY.')}
              </div>
            ) : (
              <ErrorBanner
                title="Failed to load RDS instances"
                message={rdsError instanceof Error ? rdsError.message : 'Request failed'}
                onRetry={rdsRefetch}
              />
            );
          })()
        ) : rdsItems.length === 0 ? (
          <div className="rounded-2xl border border-slate-200/60 bg-surface-50 px-5 py-10 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            <HardDrive className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium text-slate-600 dark:text-slate-300">
              No RDS instances tagged <code className="font-mono">Environment=dev</code>
            </p>
            <p className="text-sm mt-2 text-slate-500 dark:text-slate-400">
              Tag a dev RDS instance to manage it here:
            </p>
            <pre className="mt-3 mx-auto inline-block text-left rounded bg-slate-900 px-3 py-2 text-xs font-mono text-slate-100 dark:bg-slate-800">
              aws rds add-tags-to-resource \{'\n'}
              {'  '}--resource-name arn:aws:rds:REGION:ACCT:db:NAME \{'\n'}
              {'  '}--tags Key=Environment,Value=dev
            </pre>
          </div>
        ) : (
          <>
            <div className="flex gap-3 flex-wrap">
              <FilterChip
                active={rdsFilter === 'running'}
                onClick={() => setRdsFilter(rdsFilter === 'running' ? 'all' : 'running')}
                count={rdsRunningCount}
                label="running"
                tone="emerald"
              />
              <FilterChip
                active={rdsFilter === 'stopped'}
                onClick={() => setRdsFilter(rdsFilter === 'stopped' ? 'all' : 'stopped')}
                count={rdsStoppedCount}
                label="stopped"
                tone="slate"
              />
              <FilterChip
                active={rdsFilter === 'all'}
                onClick={() => setRdsFilter('all')}
                count={rdsItems.length}
                label="total"
                tone="neutral"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {rdsFiltered.map((inst) => (
                <RDSCard
                  key={inst.db_identifier}
                  instance={inst}
                  onStart={handleRDSStart}
                  onStop={handleRDSStop}
                  isActing={actingRDS === inst.db_identifier}
                />
              ))}
            </div>

            {rdsFiltered.length === 0 && (
              <div className="text-center py-10 text-slate-400 dark:text-slate-500">
                <HardDrive className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-base font-medium">No RDS instances match the current filter</p>
                <button
                  onClick={() => setRdsFilter('all')}
                  className="mt-3 text-sm text-slate-600 underline hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  Show all instances
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
